// src/components/AlertsTab.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ResolvedAlertsModal from './ResolvedAlertsModal'; // Assuming this path is correct
import { getToken } from '../utils/auth';
import Modal, { ModalCloseButton } from './ui/Modal';

const AlertsTab = ({ alerts: initialAlerts, setAlerts, API_BASE_URL, loggedInUserId, pmrn }) => {
    // Local states for alerts UI (confirm dialog, editing, inputs, resolved modal)
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [addingRecText, setAddingRecText] = useState({});
    const [addingNoteText, setAddingNoteText] = useState({});
    const [allResolvedAlertsData, setAllResolvedAlertsData] = useState([]);
    const [resolvedAlertsLoading, setResolvedAlertsLoading] = useState(false);
    const [showResolvedModal, setShowResolvedModal] = useState(false);

    // Sorting state for the alerts table
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // NEW: Loading state for initial enrichment (only true when there are alerts to enrich)
    const [isEnriching, setIsEnriching] = useState(
        () => !!(initialAlerts && initialAlerts.length > 0)
    );

    // NEW: Local state for enriched alerts (avoids prop loops)
    const [enrichedAlerts, setEnrichedAlerts] = useState([]);

    // NEW: Ref to track which alerts we've already enriched (prevents duplicate enrichment)
    const hasEnrichedRef = useRef(null);

    // Helper: pick latest task for an alert from a pre-fetched task list
    const pickLatestTaskForAlert = useCallback((alert, alertTasks) => {
        if (!alert?.id) {
            return {
                ...alert,
                latestTask: { id: null, recommendations: [], notes: [] },
            };
        }

        const tasksForAlert = (alertTasks || []).filter(
            (task) => task.alert_id === alert.id
        );

        if (tasksForAlert.length === 0) {
            return {
                ...alert,
                latestTask: { id: null, recommendations: [], notes: [] },
            };
        }

        const sortedTasks = [...tasksForAlert].sort((a, b) => b.id - a.id);
        const taskWithContent = sortedTasks.find((task) => {
            const recs = task.recommendations || [];
            const notes = task.notes || [];
            return recs.length > 0 || notes.length > 0;
        });
        const latestTask = taskWithContent || sortedTasks[0];

        return {
            ...alert,
            latestTask: {
                id: latestTask.id,
                recommendations: latestTask.recommendations || [],
                notes: latestTask.notes || [],
            },
        };
    }, []);

    const enrichAlertWithLatestTask = useCallback(async (alert) => {
        if (!alert?.id) {
            return alert;
        }
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const taskRes = await axios.get(
            `${API_BASE_URL}/tasks/?alert_id=${alert.id}&summary=true`,
            { headers }
        );
        const alertTasks = Array.isArray(taskRes.data) ? taskRes.data : [];
        return pickLatestTaskForAlert(alert, alertTasks);
    }, [API_BASE_URL, pickLatestTaskForAlert]);

    // FIXED: Enrich alerts when initialAlerts changes (but avoid duplicate enrichment)
    useEffect(() => {
        const enrichAllAlerts = async () => {
            if (!initialAlerts || initialAlerts.length === 0) {
                setIsEnriching(false);
                setEnrichedAlerts([]);
                return;
            }

            // Check if we've already enriched these alerts (compare alert IDs)
            const currentAlertIds = initialAlerts.map(a => a.id).sort().join(',');
            const lastEnrichedIds = hasEnrichedRef.current;
            if (lastEnrichedIds === currentAlertIds) {
                setIsEnriching(false);
                return;
            }

            // console.log('AlertsTab: Starting enrichment for', initialAlerts.length, 'alerts');
            hasEnrichedRef.current = currentAlertIds;
            setIsEnriching(true);

            try {
                const token = getToken();
                if (!token) {
                    setEnrichedAlerts(initialAlerts);
                    return;
                }
                const headers = { Authorization: `Bearer ${token}` };
                const taskUrl = pmrn
                    ? `${API_BASE_URL}/tasks/?pmrn=${pmrn}&summary=true`
                    : `${API_BASE_URL}/tasks/?summary=true&limit=1000`;
                const taskRes = await axios.get(taskUrl, { headers });
                const allTasks = Array.isArray(taskRes.data) ? taskRes.data : [];

                const enriched = initialAlerts.map((alert) =>
                    pickLatestTaskForAlert(alert, allTasks)
                );

                setEnrichedAlerts(enriched);
            } catch (error) {
                console.error('AlertsTab: Error enriching alerts:', error);
                setEnrichedAlerts(initialAlerts);
            } finally {
                setIsEnriching(false);
            }
        };

        enrichAllAlerts();
    }, [initialAlerts, pickLatestTaskForAlert, API_BASE_URL, pmrn]);

    // Handler for updating alert status (FIXED: Update local + parent safely)
    const handleUpdateAlertStatus = async (alertId, newStatus) => {
        try {
            const alertRes = await fetch(`${API_BASE_URL}/alerts/${alertId}/current_status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    current_status: newStatus,
                    updated_by: loggedInUserId
                })
            });

            if (!alertRes.ok) {
                const error = await alertRes.json();
                console.error('Failed to update alert status:', error);
                alert(error.detail || 'Failed to update alert status');
                return;
            }

            // Update LOCAL state first (instant UI)
            setEnrichedAlerts(prev => prev.map(a =>
                a.id === alertId ? {
                    ...a,
                    current_status: newStatus,
                    is_acknowledged: newStatus === 'Acknowledged' ? 1 : 0
                } : a
            ));

            // If status is Resolved, remove from local state
            if (newStatus === 'Resolved') {
                setEnrichedAlerts(prev => prev.filter(a => a.id !== alertId));
            }

            // Sync to parent (safe, won't trigger re-enrichment due to ref flag)
            if (setAlerts) {
                setAlerts(prev => {
                    if (newStatus === 'Resolved') {
                        return prev.filter(a => a.id !== alertId);
                    } else {
                        return prev.map(a =>
                            a.id === alertId ? {
                                ...a,
                                current_status: newStatus,
                                is_acknowledged: newStatus === 'Acknowledged' ? 1 : 0
                            } : a
                        );
                    }
                });
            }

            alert(`Alert status updated to ${newStatus}`);
        } catch (err) {
            console.error('Error updating alert status:', err);
            alert('Error updating alert status');
        }
    };

    // Resolved alerts (unchanged)
    const handleShowResolvedAlerts = async () => {
        if (!pmrn) return;
        setResolvedAlertsLoading(true);

        try {
            const res = await axios.get(`${API_BASE_URL}/resolved_alerts/patient/${pmrn}`);
            setAllResolvedAlertsData(res.data);
            setShowResolvedModal(true);
        } catch (error) {
            console.error("Error fetching resolved alerts:", error);
        } finally {
            setResolvedAlertsLoading(false);
        }
    };

    // Helper to get task ID (create if missing) - FIXED: Use local enrichedAlerts
    const getTaskId = async (alertId) => {
        console.log('getTaskId called with alertId:', alertId);
        if (!alertId) {
            console.error('getTaskId: alertId is undefined!');
            return null;
        }

        const alert = enrichedAlerts.find(a => a.id === alertId) || initialAlerts.find(a => a.id === alertId);
        if (!alert?.latestTask?.id) {
            try {
                const res = await axios.post(`${API_BASE_URL}/alerts/${alertId}/ensure-task`);
                console.log('Created task ID:', res.data.task_id);
                return res.data.task_id;
            } catch (error) {
                console.error('Error creating task:', error);
                alert('Failed to create task for alert');
                return null;
            }
        }
        return alert.latestTask.id;
    };

    // Add Item - FIXED: Update local state first
    const handleAddItem = async (type, alertId, text) => {
        console.log('handleAddItem:', type, alertId, text);
        if (!text.trim() || !alertId) {
            console.error('handleAddItem: Missing text or alertId');
            return;
        }

        const taskId = await getTaskId(alertId);
        if (!taskId) return;

        try {
            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
            await axios.post(`${API_BASE_URL}/tasks/${taskId}/${endpoint}`, {
                text,
                created_by: loggedInUserId
            });

            // Re-fetch and enrich THIS alert only
            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

            // Update LOCAL state (instant UI)
            setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

            // Sync to parent (safe)
            if (setAlerts) {
                setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
            }

            // Clear the correct input
            if (type === 'rec') {
                setAddingRecText(prev => ({...prev, [alertId]:''}));
            } else {
                setAddingNoteText(prev => ({...prev, [alertId]:''}));
            }

            alert(`Added ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
        } catch (error) {
            console.error(`Error adding ${type}:`, error);
            alert(`Failed to add ${type === 'rec' ? 'recommendation' : 'note'}`);
        }
    };

    // Edit Item (unchanged)
    const handleEditItem = (type, alertId, itemId, currentText) => {
        console.log('handleEditItem:', type, alertId, itemId);
        setEditingItem({ type, alertId, itemId, text: currentText });
    };

    // Save Edit - FIXED: Update local state first
    const handleSaveEdit = async (type, itemId, newText) => {
        if (!editingItem?.alertId) {
            console.error('handleSaveEdit: Missing alertId');
            return;
        }
        const { alertId } = editingItem;

        const taskId = await getTaskId(alertId);
        if (!taskId) return;

        try {
            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
            await axios.put(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}`, {
                text: newText,
                updated_by: loggedInUserId
            });

            // Re-fetch and enrich THIS alert only
            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

            // Update LOCAL state
            setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

            // Sync to parent
            if (setAlerts) {
                setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
            }

            setEditingItem(null);
            alert(`Updated ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
        } catch (error) {
            console.error(`Error updating ${type}:`, error);
            alert(`Failed to update ${type === 'rec' ? 'recommendation' : 'note'}`);
        }
    };

    // Delete Item (unchanged)
    const handleDeleteItem = (type, alertId, itemId) => {
        console.log('handleDeleteItem:', type, alertId, itemId);
        if (!alertId) {
            console.error('handleDeleteItem: Missing alertId');
            return;
        }
        setConfirmMessage(`Delete this ${type === 'rec' ? 'recommendation' : 'note'}?`);
        setConfirmAction({ type, alertId, itemId });
        setShowConfirm(true);
    };

    // Confirm Delete - FIXED: Update local state first
    const handleConfirmDelete = async () => {
        if (!confirmAction) return;
        const { type, alertId, itemId } = confirmAction;
        
        const taskId = await getTaskId(alertId);
        if (!taskId) {
            setShowConfirm(false);
            setConfirmAction(null);
            setConfirmMessage('');
            return;
        }

        try {
            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
            await axios.delete(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}?deleted_by=${loggedInUserId}`);
            
            // Re-fetch and enrich THIS alert only
            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

            // Update LOCAL state
            setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

            // Sync to parent
            if (setAlerts) {
                setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
            }

            alert(`Deleted ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
        } catch (error) {
            console.error(`Error deleting ${type}:`, error);
            alert(`Failed to delete ${type === 'rec' ? 'recommendation' : 'note'}`);
        }
        
        // Close dialog
        setShowConfirm(false);
        setConfirmAction(null);
        setConfirmMessage('');
    };

    // Cancel Delete (unchanged)
    const handleCancelDelete = () => {
        setShowConfirm(false);
        setConfirmAction(null);
        setConfirmMessage('');
    };
    
    // Format Recommendations (unchanged)
    const formatRecommendations = (items, alertId) => {
        //console.log('formatRecommendations called with alertId:', alertId, 'items:', items);
        // Show ALL items (including inactive/deleted ones)
        const activeItems = items || [];
    
        return (
            <div className="text-xs text-gray-700 space-y-1">
                {/* Add Input */}
                <div className="flex space-x-1 mb-1">
                    <input
                        type="text"
                        value={addingRecText[alertId] || ''}
                        onChange={(e) => setAddingRecText(prev => ({ ...prev, [alertId]: e.target.value }))}
                        placeholder="Add recommendation.."
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                    <button
                        onClick={() => handleAddItem('rec', alertId, addingRecText[alertId] || '')}
                        disabled={!(addingRecText[alertId] || '').trim()}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>

                {/* List existing items OR show "No recommendations" */}
                {activeItems && activeItems.length > 0 ? (
                    <ul className="space-y-0.5 max-h-16 overflow-y-auto">
                        {activeItems.map((item) => {
                            const isEditing = editingItem?.type === 'rec' && editingItem?.itemId === item.id;
                            return (
                                <li key={item.id} className="flex justify-between items-center space-x-2">
                                    {isEditing ? (
                                        <div className="flex space-x-1 flex-1">
                                            <input
                                                type="text"
                                                value={editingItem.text}
                                                onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                                                className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
                                            />
                                            <button
                                                onClick={() => handleSaveEdit('rec', item.id, editingItem.text)}
                                                className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingItem(null)}
                                                className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex-1">
                                            <span className="font-medium">{item.text}</span>
                                            <span className="text-gray-400 text-[10px] ml-1">
                                                ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
                                            </span>
                                        </div>
                                    )}
                                    {!isEditing && (
                                        <div className="space-x-1">
                                            <button
                                                onClick={() => handleEditItem('rec', alertId, item.id, item.text)}
                                                className="text-blue-500 text-[10px] hover:text-blue-700"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteItem('rec', alertId, item.id)}
                                                className="text-red-500 text-[10px] hover:text-red-700"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <span className="text-gray-500 italic text-xs">No recommendations</span>
                )}
            </div>
        );
    };

    // Format Notes (unchanged)
    const formatNotes = (items, alertId) => {
        //console.log('formatNotes called with alertId:', alertId, 'items:', items);
        // Show ALL items (including inactive/deleted ones)
        const activeItems = items || [];
        
        return (
            <div className="text-xs text-gray-700 space-y-1">
                {/* Add Input */}
                <div className="flex space-x-1 mb-1">
                    <input
                        type="text"
                        value={addingNoteText[alertId] || ''}
                        onChange={(e) => setAddingNoteText(prev => ({ ...prev, [alertId]: e.target.value }))}
                        placeholder="Add note..."
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                    <button
                        onClick={() => handleAddItem('note', alertId, addingNoteText[alertId] || '')}
                        disabled={!(addingNoteText[alertId] || '').trim()}
                        className="bg-green-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>

                {/* List existing items OR show "No notes" */}
                {activeItems && activeItems.length > 0 ? (
                    <ul className="space-y-0.5 max-h-16 overflow-y-auto">
                        {activeItems.map((item) => {
                            const isEditing = editingItem?.type === 'note' && editingItem?.itemId === item.id;
                            return (
                                <li key={item.id} className="flex justify-between items-center space-x-2">
                                    {isEditing ? (
                                        <div className="flex space-x-1 flex-1">
                                            <input
                                                type="text"
                                                value={editingItem.text}
                                                onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                                                className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
                                            />
                                            <button
                                                onClick={() => handleSaveEdit('note', item.id, editingItem.text)}
                                                className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingItem(null)}
                                                className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex-1">
                                            <span className="font-medium">{item.text}</span>
                                            <span className="text-gray-400 text-[10px] ml-1">
                                                ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
                                            </span>
                                        </div>
                                    )}
                                    {!isEditing && (
                                        <div className="space-x-1">
                                            <button
                                                onClick={() => handleEditItem('note', alertId, item.id, item.text)}
                                                className="text-blue-500 text-[10px] hover:text-blue-700"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteItem('note', alertId, item.id)}
                                                className="text-red-500 text-[10px] hover:text-red-700"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <span className="text-gray-500 italic text-xs">No notes</span>
                )}
            </div>
        );
    };

    // Helper functions (formatDate, formatTime) - unchanged
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return 'N/A';
        }
    };

    // Toggle sorting for a column: asc -> desc -> off
    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key !== key) return { key, direction: 'asc' };
            if (prev.direction === 'asc') return { key, direction: 'desc' };
            return { key: null, direction: 'asc' };
        });
    };

    // Sort value extractor per column
    const getSortValue = (alert, key) => {
        switch (key) {
            case 'priority': {
                // Sort by severity order instead of alphabetically
                const order = { critical: 0, high: 1, medium: 2, low: 3 };
                const p = (alert.priority || '').toLowerCase();
                return order[p] !== undefined ? order[p] : 99;
            }
            case 'description':
                return (alert.description || '').toLowerCase();
            case 'date':
                return alert.created_at ? new Date(alert.created_at).getTime() : 0;
            case 'status':
                return (alert.current_status || (alert.is_acknowledged ? 'Acknowledged' : 'Pending')).toLowerCase();
            default:
                return '';
        }
    };

    const visibleAlerts = enrichedAlerts.filter(alert => {
        const status = (alert.current_status || '').toLowerCase();
        return status !== 'resolved';
    });

    const sortedAlerts = sortConfig.key
        ? [...visibleAlerts].sort((a, b) => {
            const aVal = getSortValue(a, sortConfig.key);
            const bVal = getSortValue(b, sortConfig.key);
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        })
        : visibleAlerts;

    // Clickable header cell with sort arrow indicator
    const SortableHeader = ({ label, sortKey }) => {
        const isActive = sortConfig.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                title={`Sort by ${label}`}
            >
                <span className="inline-flex items-center gap-1">
                    {label}
                    <span className={`text-xs ${isActive ? 'text-teal-600' : 'text-gray-300'}`}>
                        {isActive ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                </span>
            </th>
        );
    };

    if (isEnriching && initialAlerts?.length > 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-gray-600 font-medium">Loading patient alerts...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Alerts</h3>
                <button
                    onClick={handleShowResolvedAlerts}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                    disabled={resolvedAlertsLoading}
                >
                    {resolvedAlertsLoading ? 'Loading...' : 'View Resolved Alerts'}
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">S.No</th>
                            <SortableHeader label="Priority" sortKey="priority" />
                            <SortableHeader label="Description" sortKey="description" />
                            <SortableHeader label="Date" sortKey="date" />
                            <SortableHeader label="Time" sortKey="date" />
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Recommendations</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Notes</th>
                            <SortableHeader label="Status" sortKey="status" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {sortedAlerts.length > 0 ? (
                            sortedAlerts.map((alert, index) => {
                                const latestTask = alert.latestTask || {};
                                const recs = latestTask.recommendations || [];
                                const notes = latestTask.notes || [];

                                //console.log(`Alert ${alert.id}: recs=${recs.length}, notes=${notes.length}`);

                                return (
                                    <tr key={alert.id ?? index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{alert.priority}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{alert.description}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(alert.created_at)}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{formatTime(alert.created_at)}</td>
                                        <td className="px-4 py-3">
                                            {formatRecommendations(recs, alert.id)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {formatNotes(notes, alert.id)}
                                        </td>
                                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                            <select
                                                className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500"
                                                value={alert.current_status || (alert.is_acknowledged ? 'Acknowledged' : 'Pending')}
                                                onChange={(e) => handleUpdateAlertStatus(alert.id, e.target.value)}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Acknowledged">Acknowledged</option>
                                                <option value="Resolved">Resolved</option>
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} className="text-center py-4 text-gray-500">
                                    No Alerts found for this patient.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirm Dialog (unchanged) */}
            <Modal open={showConfirm} onClose={handleCancelDelete}>
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-gray-100">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                            <ModalCloseButton onClose={handleCancelDelete} />
                        </div>
                        <p className="text-gray-600 mb-6">{confirmMessage}</p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleCancelDelete}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
            </Modal>

            {showResolvedModal && (
                <ResolvedAlertsModal 
                    onClose={() => setShowResolvedModal(false)} 
                    resolvedAlerts={allResolvedAlertsData}
                />
            )}
        </div>
    );
};

export default AlertsTab;


// // src/components/AlertsTab.js
// import React, { useState, useEffect, useRef } from 'react';
// import axios from 'axios';
// import ResolvedAlertsModal from './ResolvedAlertsModal'; // Assuming this path is correct

// const AlertsTab = ({ alerts: initialAlerts, setAlerts, API_BASE_URL, loggedInUserId, pmrn }) => {
//     // Local states for alerts UI (confirm dialog, editing, inputs, resolved modal)
//     const [showConfirm, setShowConfirm] = useState(false);
//     const [confirmAction, setConfirmAction] = useState(null);
//     const [confirmMessage, setConfirmMessage] = useState('');
//     const [editingItem, setEditingItem] = useState(null);
//     const [addingRecText, setAddingRecText] = useState({});
//     const [addingNoteText, setAddingNoteText] = useState({});
//     const [allResolvedAlertsData, setAllResolvedAlertsData] = useState([]);
//     const [resolvedAlertsLoading, setResolvedAlertsLoading] = useState(false);
//     const [showResolvedModal, setShowResolvedModal] = useState(false);

//     // NEW: Loading state for initial enrichment
//     const [isEnriching, setIsEnriching] = useState(true);

//     // NEW: Local state for enriched alerts (avoids prop loops)
//     const [enrichedAlerts, setEnrichedAlerts] = useState([]);

//     // NEW: Ref to run enrichment only ONCE on mount (prevents infinite loop)
//     const hasEnrichedRef = useRef(false);

//     // FIXED: Enrich all initial alerts ONCE on mount (no deps to avoid loops)
//     useEffect(() => {
//         const enrichAllAlerts = async () => {
//             if (hasEnrichedRef.current || !initialAlerts || initialAlerts.length === 0) {
//                 setIsEnriching(false);
//                 return;
//             }

//            // console.log('AlertsTab: Starting one-time enrichment for', initialAlerts.length, 'alerts');
//             hasEnrichedRef.current = true;
//             setIsEnriching(true);

//             try {
//                 const enriched = await Promise.all(
//                     initialAlerts.map(async (alert) => {
//                         //console.log('AlertsTab: Enriching alert', alert.id);
//                         return await enrichAlertWithLatestTask(alert);
//                     })
//                 );

//                 // Set LOCAL state (no parent update here to avoid loop)
//                 setEnrichedAlerts(enriched);
//                 //console.log('AlertsTab: Enrichment complete. Enriched alerts:', enriched.map(a => ({ id: a.id, recs: (a.latestTask?.recommendations || []).length, notes: (a.latestTask?.notes || []).length })));
//             } catch (error) {
//                 console.error('AlertsTab: Error enriching alerts:', error);
//                 // Fallback: Use raw initialAlerts
//                 setEnrichedAlerts(initialAlerts);
//             } finally {
//                 setIsEnriching(false);
//             }
//         };

//         enrichAllAlerts();
//     }, []); // EMPTY deps: Run ONLY once on mount

//     // Handler for updating alert status (FIXED: Update local + parent safely)
//     const handleUpdateAlertStatus = async (alertId, newStatus) => {
//         try {
//             const alertRes = await fetch(`${API_BASE_URL}/alerts/${alertId}/current_status`, {
//                 method: 'PUT',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({
//                     current_status: newStatus,
//                     updated_by: loggedInUserId
//                 })
//             });

//             if (!alertRes.ok) {
//                 const error = await alertRes.json();
//                 console.error('Failed to update alert status:', error);
//                 alert(error.detail || 'Failed to update alert status');
//                 return;
//             }

//             // Update LOCAL state first (instant UI)
//             setEnrichedAlerts(prev => prev.map(a =>
//                 a.id === alertId ? {
//                     ...a,
//                     current_status: newStatus,
//                     is_acknowledged: newStatus === 'Acknowledged' ? 1 : 0
//                 } : a
//             ));

//             // If status is Resolved, remove from local state
//             if (newStatus === 'Resolved') {
//                 setEnrichedAlerts(prev => prev.filter(a => a.id !== alertId));
//             }

//             // Sync to parent (safe, won't trigger re-enrichment due to ref flag)
//             if (setAlerts) {
//                 setAlerts(prev => {
//                     if (newStatus === 'Resolved') {
//                         return prev.filter(a => a.id !== alertId);
//                     } else {
//                         return prev.map(a =>
//                             a.id === alertId ? {
//                                 ...a,
//                                 current_status: newStatus,
//                                 is_acknowledged: newStatus === 'Acknowledged' ? 1 : 0
//                             } : a
//                         );
//                     }
//                 });
//             }

//             alert(`Alert status updated to ${newStatus}`);
//         } catch (err) {
//             console.error('Error updating alert status:', err);
//             alert('Error updating alert status');
//         }
//     };

//     // Resolved alerts (unchanged)
//     const handleShowResolvedAlerts = async () => {
//         if (!pmrn) return;
//         setResolvedAlertsLoading(true);

//         try {
//             const res = await axios.get(`${API_BASE_URL}/resolved_alerts/patient/${pmrn}`);
//             setAllResolvedAlertsData(res.data);
//             setShowResolvedModal(true);
//         } catch (error) {
//             console.error("Error fetching resolved alerts:", error);
//         } finally {
//             setResolvedAlertsLoading(false);
//         }
//     };

//     // Helper to enrich a single alert with latest task (unchanged)
//     const enrichAlertWithLatestTask = async (alert) => {
//         if (!alert?.id) {
//             console.warn('AlertsTab: Skipping alert with no ID:', alert);
//             return alert;
//         }

//         try {
//             const taskRes = await axios.get(`${API_BASE_URL}/tasks/?alert_id=${alert.id}`);
//             //console.log(`AlertsTab: Tasks for alert ${alert.id}:`, taskRes.data);

//             // Filter tasks that belong to THIS alert only
//             const alertTasks = taskRes.data.filter(task => task.alert_id === alert.id);

//             if (alertTasks.length === 0) {
//                 console.log(`AlertsTab: No tasks found for alert ${alert.id}`);
//                 return { 
//                     ...alert, 
//                     latestTask: {
//                         id: null,
//                         recommendations: [],
//                         notes: []
//                     }
//                 };
//             }

//             // Sort by ID descending to get the latest task
//             const sortedTasks = alertTasks.sort((a, b) => b.id - a.id);

//             // Find the task with recommendations OR notes, otherwise use the latest
//             const taskWithContent = sortedTasks.find(task => {
//                 const recs = task.recommendations || [];
//                 const notes = task.notes || [];
//                 const activeRecs = recs.filter(r => r.is_active !== false);
//                 const activeNotes = notes.filter(n => n.is_active !== false);
//                 return activeRecs.length > 0 || activeNotes.length > 0;
//             });

//             const latestTask = taskWithContent || sortedTasks[0];

//             //console.log("AlertsTab: Selected task object for alert", alert.id, ":", latestTask);

//             let recommendations = latestTask.recommendations || [];
//             let notes = latestTask.notes || [];

//             recommendations = recommendations.filter(r => r.is_active !== false);
//             notes = notes.filter(n => n.is_active !== false);

//             //console.log(`AlertsTab: Alert ${alert.id}: Task ${latestTask.id} has ${recommendations.length} recs, ${notes.length} notes`);

//             return {
//                 ...alert,
//                 latestTask: {
//                     id: latestTask.id,
//                     recommendations,
//                     notes
//                 }
//             };
//         } catch (error) {
//             console.warn(`AlertsTab: Error fetching task for alert ${alert.id}:`, error.message);
//             return { 
//                 ...alert, 
//                 latestTask: {
//                     id: null,
//                     recommendations: [],
//                     notes: []
//                 }
//             };
//         }
//     };

//     // Helper to get task ID (create if missing) - FIXED: Use local enrichedAlerts
//     const getTaskId = async (alertId) => {
//         console.log('getTaskId called with alertId:', alertId);
//         if (!alertId) {
//             console.error('getTaskId: alertId is undefined!');
//             return null;
//         }

//         const alert = enrichedAlerts.find(a => a.id === alertId) || initialAlerts.find(a => a.id === alertId);
//         if (!alert?.latestTask?.id) {
//             try {
//                 const res = await axios.post(`${API_BASE_URL}/alerts/${alertId}/ensure-task`);
//                 console.log('Created task ID:', res.data.task_id);
//                 return res.data.task_id;
//             } catch (error) {
//                 console.error('Error creating task:', error);
//                 alert('Failed to create task for alert');
//                 return null;
//             }
//         }
//         return alert.latestTask.id;
//     };

//     // Add Item - FIXED: Update local state first
//     const handleAddItem = async (type, alertId, text) => {
//         console.log('handleAddItem:', type, alertId, text);
//         if (!text.trim() || !alertId) {
//             console.error('handleAddItem: Missing text or alertId');
//             return;
//         }

//         const taskId = await getTaskId(alertId);
//         if (!taskId) return;

//         try {
//             const endpoint = type === 'rec' ? 'recommendations' : 'notes';
//             await axios.post(`${API_BASE_URL}/tasks/${taskId}/${endpoint}`, {
//                 text,
//                 created_by: loggedInUserId
//             });

//             // Re-fetch and enrich THIS alert only
//             const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

//             // Update LOCAL state (instant UI)
//             setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

//             // Sync to parent (safe)
//             if (setAlerts) {
//                 setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
//             }

//             // Clear the correct input
//             if (type === 'rec') {
//                 setAddingRecText(prev => ({...prev, [alertId]:''}));
//             } else {
//                 setAddingNoteText(prev => ({...prev, [alertId]:''}));
//             }

//             alert(`Added ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
//         } catch (error) {
//             console.error(`Error adding ${type}:`, error);
//             alert(`Failed to add ${type === 'rec' ? 'recommendation' : 'note'}`);
//         }
//     };

//     // Edit Item (unchanged)
//     const handleEditItem = (type, alertId, itemId, currentText) => {
//         console.log('handleEditItem:', type, alertId, itemId);
//         setEditingItem({ type, alertId, itemId, text: currentText });
//     };

//     // Save Edit - FIXED: Update local state first
//     const handleSaveEdit = async (type, itemId, newText) => {
//         if (!editingItem?.alertId) {
//             console.error('handleSaveEdit: Missing alertId');
//             return;
//         }
//         const { alertId } = editingItem;

//         const taskId = await getTaskId(alertId);
//         if (!taskId) return;

//         try {
//             const endpoint = type === 'rec' ? 'recommendations' : 'notes';
//             await axios.put(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}`, {
//                 text: newText,
//                 updated_by: loggedInUserId
//             });

//             // Re-fetch and enrich THIS alert only
//             const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

//             // Update LOCAL state
//             setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

//             // Sync to parent
//             if (setAlerts) {
//                 setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
//             }

//             setEditingItem(null);
//             alert(`Updated ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
//         } catch (error) {
//             console.error(`Error updating ${type}:`, error);
//             alert(`Failed to update ${type === 'rec' ? 'recommendation' : 'note'}`);
//         }
//     };

//     // Delete Item (unchanged)
//     const handleDeleteItem = (type, alertId, itemId) => {
//         console.log('handleDeleteItem:', type, alertId, itemId);
//         if (!alertId) {
//             console.error('handleDeleteItem: Missing alertId');
//             return;
//         }
//         setConfirmMessage(`Delete this ${type === 'rec' ? 'recommendation' : 'note'}?`);
//         setConfirmAction({ type, alertId, itemId });
//         setShowConfirm(true);
//     };

//     // Confirm Delete - FIXED: Update local state first
//     const handleConfirmDelete = async () => {
//         if (!confirmAction) return;
//         const { type, alertId, itemId } = confirmAction;
        
//         const taskId = await getTaskId(alertId);
//         if (!taskId) {
//             setShowConfirm(false);
//             setConfirmAction(null);
//             setConfirmMessage('');
//             return;
//         }

//         try {
//             const endpoint = type === 'rec' ? 'recommendations' : 'notes';
//             await axios.delete(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}?deleted_by=${loggedInUserId}`);
            
//             // Re-fetch and enrich THIS alert only
//             const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));

//             // Update LOCAL state
//             setEnrichedAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));

//             // Sync to parent
//             if (setAlerts) {
//                 setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
//             }

//             alert(`Deleted ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
//         } catch (error) {
//             console.error(`Error deleting ${type}:`, error);
//             alert(`Failed to delete ${type === 'rec' ? 'recommendation' : 'note'}`);
//         }
        
//         // Close dialog
//         setShowConfirm(false);
//         setConfirmAction(null);
//         setConfirmMessage('');
//     };

//     // Cancel Delete (unchanged)
//     const handleCancelDelete = () => {
//         setShowConfirm(false);
//         setConfirmAction(null);
//         setConfirmMessage('');
//     };
    
//     // Format Recommendations (unchanged)
//     const formatRecommendations = (items, alertId) => {
//         //console.log('formatRecommendations called with alertId:', alertId, 'items:', items);
    
//         return (
//             <div className="text-xs text-gray-700 space-y-1">
//                 {/* Add Input */}
//                 <div className="flex space-x-1 mb-1">
//                     <input
//                         type="text"
//                         value={addingRecText[alertId] || ''}
//                         onChange={(e) => setAddingRecText(prev => ({ ...prev, [alertId]: e.target.value }))}
//                         placeholder="Add recommendation.."
//                         className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
//                     />
//                     <button
//                         onClick={() => handleAddItem('rec', alertId, addingRecText[alertId] || '')}
//                         disabled={!(addingRecText[alertId] || '').trim()}
//                         className="bg-blue-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
//                     >
//                         Add
//                     </button>
//                 </div>

//                 {/* List existing items OR show "No recommendations" */}
//                 {items && items.length > 0 ? (
//                     <ul className="space-y-0.5 max-h-16 overflow-y-auto">
//                         {items.map((item) => {
//                             const isEditing = editingItem?.type === 'rec' && editingItem?.itemId === item.id;
//                             return (
//                                 <li key={item.id} className="flex justify-between items-center space-x-2">
//                                     {isEditing ? (
//                                         <div className="flex space-x-1 flex-1">
//                                             <input
//                                                 type="text"
//                                                 value={editingItem.text}
//                                                 onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
//                                                 className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
//                                             />
//                                             <button
//                                                 onClick={() => handleSaveEdit('rec', item.id, editingItem.text)}
//                                                 className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
//                                             >
//                                                 Save
//                                             </button>
//                                             <button
//                                                 onClick={() => setEditingItem(null)}
//                                                 className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
//                                             >
//                                                 Cancel
//                                             </button>
//                                         </div>
//                                     ) : (
//                                         <div className="flex-1">
//                                             <span className="font-medium">{item.text}</span>
//                                             <span className="text-gray-400 text-[10px] ml-1">
//                                                 ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
//                                             </span>
//                                         </div>
//                                     )}
//                                     {!isEditing && (
//                                         <div className="space-x-1">
//                                             <button
//                                                 onClick={() => handleEditItem('rec', alertId, item.id, item.text)}
//                                                 className="text-blue-500 text-[10px] hover:text-blue-700"
//                                             >
//                                                 Edit
//                                             </button>
//                                             <button
//                                                 onClick={() => handleDeleteItem('rec', alertId, item.id)}
//                                                 className="text-red-500 text-[10px] hover:text-red-700"
//                                             >
//                                                 Delete
//                                             </button>
//                                         </div>
//                                     )}
//                                 </li>
//                             );
//                         })}
//                     </ul>
//                 ) : (
//                     <span className="text-gray-500 italic text-xs">No recommendations</span>
//                 )}
//             </div>
//         );
//     };

//     // Format Notes (unchanged)
//     const formatNotes = (items, alertId) => {
//         //console.log('formatNotes called with alertId:', alertId, 'items:', items);
        
//         return (
//             <div className="text-xs text-gray-700 space-y-1">
//                 {/* Add Input */}
//                 <div className="flex space-x-1 mb-1">
//                     <input
//                         type="text"
//                         value={addingNoteText[alertId] || ''}
//                         onChange={(e) => setAddingNoteText(prev => ({ ...prev, [alertId]: e.target.value }))}
//                         placeholder="Add note..."
//                         className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
//                     />
//                     <button
//                         onClick={() => handleAddItem('note', alertId, addingNoteText[alertId] || '')}
//                         disabled={!(addingNoteText[alertId] || '').trim()}
//                         className="bg-green-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
//                     >
//                         Add
//                     </button>
//                 </div>

//                 {/* List existing items OR show "No notes" */}
//                 {items && items.length > 0 ? (
//                     <ul className="space-y-0.5 max-h-16 overflow-y-auto">
//                         {items.map((item) => {
//                             const isEditing = editingItem?.type === 'note' && editingItem?.itemId === item.id;
//                             return (
//                                 <li key={item.id} className="flex justify-between items-center space-x-2">
//                                     {isEditing ? (
//                                         <div className="flex space-x-1 flex-1">
//                                             <input
//                                                 type="text"
//                                                 value={editingItem.text}
//                                                 onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
//                                                 className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
//                                             />
//                                             <button
//                                                 onClick={() => handleSaveEdit('note', item.id, editingItem.text)}
//                                                 className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
//                                             >
//                                                 Save
//                                             </button>
//                                             <button
//                                                 onClick={() => setEditingItem(null)}
//                                                 className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
//                                             >
//                                                 Cancel
//                                             </button>
//                                         </div>
//                                     ) : (
//                                         <div className="flex-1">
//                                             <span className="font-medium">{item.text}</span>
//                                             <span className="text-gray-400 text-[10px] ml-1">
//                                                 ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
//                                             </span>
//                                         </div>
//                                     )}
//                                     {!isEditing && (
//                                         <div className="space-x-1">
//                                             <button
//                                                 onClick={() => handleEditItem('note', alertId, item.id, item.text)}
//                                                 className="text-blue-500 text-[10px] hover:text-blue-700"
//                                             >
//                                                 Edit
//                                             </button>
//                                             <button
//                                                 onClick={() => handleDeleteItem('note', alertId, item.id)}
//                                                 className="text-red-500 text-[10px] hover:text-red-700"
//                                             >
//                                                 Delete
//                                             </button>
//                                         </div>
//                                     )}
//                                 </li>
//                             );
//                         })}
//                     </ul>
//                 ) : (
//                     <span className="text-gray-500 italic text-xs">No notes</span>
//                 )}
//             </div>
//         );
//     };

//     // Helper functions (formatDate, formatTime) - unchanged
//     const formatDate = (dateString) => {
//         if (!dateString) return 'N/A';
//         try {
//             const date = new Date(dateString);
//             return date.toLocaleDateString();
//         } catch {
//             return 'N/A';
//         }
//     };

//     const formatTime = (dateString) => {
//         if (!dateString) return 'N/A';
//         try {
//             const date = new Date(dateString);
//             return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
//         } catch {
//             return 'N/A';
//         }
//     };

//     if (isEnriching) {
//         return <div className="text-center py-4 text-gray-500">Loading recommendations and notes...</div>;
//     }

//     return (
//         <div>
//             <div className="flex items-center justify-between mb-4">
//                 <h3 className="text-xl font-bold">Alerts</h3>
//                 <button
//                     onClick={handleShowResolvedAlerts}
//                     className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
//                     disabled={resolvedAlertsLoading}
//                 >
//                     {resolvedAlertsLoading ? 'Loading...' : 'View Resolved Alerts'}
//                 </button>
//             </div>

//             <div className="overflow-x-auto">
//                 <table className="w-full">
//                     <thead className="bg-gray-50">
//                         <tr>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Priority</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Description</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Time</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Recommendations</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Notes</th>
//                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
//                         </tr>
//                     </thead>
//                     <tbody className="divide-y divide-gray-200">
//                         {enrichedAlerts.length > 0 ? (
//                             enrichedAlerts.map((alert, index) => {
//                                 const latestTask = alert.latestTask || {};
//                                 const recs = latestTask.recommendations || [];
//                                 const notes = latestTask.notes || [];

//                                 //console.log(`Alert ${alert.id}: recs=${recs.length}, notes=${notes.length}`);

//                                 return (
//                                     <tr key={index} className="hover:bg-gray-50">
//                                         <td className="px-4 py-3 text-sm text-gray-900">{alert.priority}</td>
//                                         <td className="px-4 py-3 text-sm text-gray-900">{alert.description}</td>
//                                         <td className="px-4 py-3 text-sm text-gray-600">{formatDate(alert.created_at)}</td>
//                                         <td className="px-4 py-3 text-sm text-gray-600">{formatTime(alert.created_at)}</td>
//                                         <td className="px-4 py-3">
//                                             {formatRecommendations(recs, alert.id)}
//                                         </td>
//                                         <td className="px-4 py-3">
//                                             {formatNotes(notes, alert.id)}
//                                         </td>
//                                         <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
//                                             <select
//                                                 className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500"
//                                                 value={alert.current_status || (alert.is_acknowledged ? 'Acknowledged' : 'Pending')}
//                                                 onChange={(e) => handleUpdateAlertStatus(alert.id, e.target.value)}
//                                             >
//                                                 <option value="Pending">Pending</option>
//                                                 <option value="Acknowledged">Acknowledged</option>
//                                                 <option value="Resolved">Resolved</option>
//                                             </select>
//                                         </td>
//                                     </tr>
//                                 );
//                             })
//                         ) : (
//                             <tr>
//                                 <td colSpan={7} className="text-center py-4 text-gray-500">
//                                     No alerts found for this patient.
//                                 </td>
//                             </tr>
//                         )}
//                     </tbody>
//                 </table>
//             </div>

//             {/* Confirm Dialog (unchanged) */}
//             {showConfirm && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//                     <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
//                         <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
//                         <p className="text-gray-600 mb-6">{confirmMessage}</p>
//                         <div className="flex justify-end space-x-3">
//                             <button
//                                 onClick={handleCancelDelete}
//                                 className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 onClick={handleConfirmDelete}
//                                 className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
//                             >
//                                 Delete
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {showResolvedModal && (
//                 <ResolvedAlertsModal 
//                     onClose={() => setShowResolvedModal(false)} 
//                     resolvedAlerts={allResolvedAlertsData}
//                 />
//             )}
//         </div>
//     );
// };

// export default AlertsTab;


// // --------------------------------------------------------------------------------------------------------------------

// //// src/components/AlertsTab.js
// //import React, { useState } from 'react';
// //import axios from 'axios';
// //import ResolvedAlertsModal from './ResolvedAlertsModal'; // Assuming this path is correct
// //
// //const AlertsTab = ({ alerts: initialAlerts, setAlerts, API_BASE_URL, loggedInUserId, pmrn }) => {
// //    // Local states for alerts UI (confirm dialog, editing, inputs, resolved modal)
// //    const [showConfirm, setShowConfirm] = useState(false);
// //    const [confirmAction, setConfirmAction] = useState(null);
// //    const [confirmMessage, setConfirmMessage] = useState('');
// //    const [editingItem, setEditingItem] = useState(null);
// //    const [addingRecText, setAddingRecText] = useState({});
// //    const [addingNoteText, setAddingNoteText] = useState({});
// //    const [allResolvedAlertsData, setAllResolvedAlertsData] = useState([]);
// //    const [resolvedAlertsLoading, setResolvedAlertsLoading] = useState(false);
// //    const [showResolvedModal, setShowResolvedModal] = useState(false);
// //
// //    // Handler for updating alert status
// //    const handleUpdateAlertStatus = async (alertId, newStatus) => {
// //        try {
// //            const alertRes = await fetch(`${API_BASE_URL}/alerts/${alertId}/current_status`, {
// //                method: 'PUT',
// //                headers: { 'Content-Type': 'application/json' },
// //                body: JSON.stringify({
// //                    current_status: newStatus,
// //                    updated_by: loggedInUserId
// //                })
// //            });
// //
// //            if (!alertRes.ok) {
// //                const error = await alertRes.json();
// //                console.error('Failed to update alert status:', error);
// //                alert(error.detail || 'Failed to update alert status');
// //                return;
// //            }
// //
// //            // If status is Resolved, remove the alert from the list
// //            if (newStatus === 'Resolved') {
// //                setAlerts(prev => prev.filter(a => a.id !== alertId));
// //            } else {
// //                // Update the alert in the list for non-resolved statuses
// //                setAlerts(prev => prev.map(a =>
// //                    a.id === alertId ? {
// //                        ...a,
// //                        current_status: newStatus,
// //                        is_acknowledged: newStatus === 'Acknowledged' ? 1 : 0
// //                    } : a
// //                ));
// //            }
// //
// //            alert(`Alert status updated to ${newStatus}`);
// //        } catch (err) {
// //            console.error('Error updating alert status:', err);
// //            alert('Error updating alert status');
// //        }
// //    };
// //
// //    // Resolved alerts
// //    const handleShowResolvedAlerts = async () => {
// //        if (!pmrn) return;
// //        setResolvedAlertsLoading(true);
// //
// //        try {
// //            const res = await axios.get(`${API_BASE_URL}/resolved_alerts/patient/${pmrn}`);
// //            setAllResolvedAlertsData(res.data);
// //            setShowResolvedModal(true);
// //        } catch (error) {
// //            console.error("Error fetching resolved alerts:", error);
// //        } finally {
// //            setResolvedAlertsLoading(false);
// //        }
// //    };
// //
// //    // Helper to enrich a single alert with latest task (same logic as fetchTertiaryData)
// //    const enrichAlertWithLatestTask = async (alert) => {
// //        if (!alert?.id) {
// //            console.warn('Skipping alert with no ID:', alert);
// //            return alert;
// //        }
// //
// //        try {
// //            const taskRes = await axios.get(`${API_BASE_URL}/tasks/?alert_id=${alert.id}`);
// //            console.log(`Tasks for alert ${alert.id}:`, taskRes.data);
// //
// //            // Filter tasks that belong to THIS alert only
// //            const alertTasks = taskRes.data.filter(task => task.alert_id === alert.id);
// //
// //            if (alertTasks.length === 0) {
// //                console.log(`No tasks found for alert ${alert.id}`);
// //                return { 
// //                    ...alert, 
// //                    latestTask: {
// //                        id: null,
// //                        recommendations: [],
// //                        notes: []
// //                    }
// //                };
// //            }
// //
// //            // Sort by ID descending to get the latest task
// //            const sortedTasks = alertTasks.sort((a, b) => b.id - a.id);
// //
// //            // Find the task with recommendations OR notes, otherwise use the latest
// //            const taskWithContent = sortedTasks.find(task => {
// //                const recs = task.recommendations || [];
// //                const notes = task.notes || [];
// //                const activeRecs = recs.filter(r => r.is_active !== false);
// //                const activeNotes = notes.filter(n => n.is_active !== false);
// //                return activeRecs.length > 0 || activeNotes.length > 0;
// //            });
// //
// //            const latestTask = taskWithContent || sortedTasks[0];
// //
// //            console.log("Selected task object:", latestTask);
// //
// //            let recommendations = latestTask.recommendations || [];
// //            let notes = latestTask.notes || [];
// //
// //            recommendations = recommendations.filter(r => r.is_active !== false);
// //            notes = notes.filter(n => n.is_active !== false);
// //
// //            console.log(`Alert ${alert.id}: Task ${latestTask.id} has ${recommendations.length} recs, ${notes.length} notes`);
// //
// //            return {
// //                ...alert,
// //                latestTask: {
// //                    id: latestTask.id,
// //                    recommendations,
// //                    notes
// //                }
// //            };
// //        } catch (error) {
// //            console.warn(`Error fetching task for alert ${alert.id}:`, error.message);
// //            return { 
// //                ...alert, 
// //                latestTask: {
// //                    id: null,
// //                    recommendations: [],
// //                    notes: []
// //                }
// //            };
// //        }
// //    };
// //
// //    // Helper to get task ID (create if missing)
// //    const getTaskId = async (alertId) => {
// //        console.log('getTaskId called with alertId:', alertId);
// //        if (!alertId) {
// //            console.error('getTaskId: alertId is undefined!');
// //            return null;
// //        }
// //
// //        const alert = initialAlerts.find(a => a.id === alertId);
// //        if (!alert?.latestTask?.id) {
// //            try {
// //                const res = await axios.post(`${API_BASE_URL}/alerts/${alertId}/ensure-task`);
// //                console.log('Created task ID:', res.data.task_id);
// //                return res.data.task_id;
// //            } catch (error) {
// //                console.error('Error creating task:', error);
// //                alert('Failed to create task for alert');
// //                return null;
// //            }
// //        }
// //        return alert.latestTask.id;
// //    };
// //
// //    // Add Item
// //    const handleAddItem = async (type, alertId, text) => {
// //        console.log('handleAddItem:', type, alertId, text);
// //        if (!text.trim() || !alertId) {
// //            console.error('handleAddItem: Missing text or alertId');
// //            return;
// //        }
// //
// //        const taskId = await getTaskId(alertId);
// //        if (!taskId) return;
// //
// //        try {
// //            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
// //            await axios.post(`${API_BASE_URL}/tasks/${taskId}/${endpoint}`, {
// //                text,
// //                created_by: loggedInUserId
// //            });
// //
// //            // FIXED: Re-fetch and enrich the alert fully (same as fetchTertiaryData logic)
// //            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));
// //            setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
// //
// //            // Clear the correct input
// //            if (type === 'rec') {
// //                setAddingRecText(prev => ({...prev, [alertId]:''}));
// //            } else {
// //                setAddingNoteText(prev => ({...prev, [alertId]:''}));
// //            }
// //
// //            alert(`Added ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
// //        } catch (error) {
// //            console.error(`Error adding ${type}:`, error);
// //            alert(`Failed to add ${type === 'rec' ? 'recommendation' : 'note'}`);
// //        }
// //    };
// //
// //    // Edit Item
// //    const handleEditItem = (type, alertId, itemId, currentText) => {
// //        console.log('handleEditItem:', type, alertId, itemId);
// //        setEditingItem({ type, alertId, itemId, text: currentText });
// //    };
// //
// //    // Save Edit
// //    const handleSaveEdit = async (type, itemId, newText) => {
// //        if (!editingItem?.alertId) {
// //            console.error('handleSaveEdit: Missing alertId');
// //            return;
// //        }
// //        const { alertId } = editingItem;
// //
// //        const taskId = await getTaskId(alertId);
// //        if (!taskId) return;
// //
// //        try {
// //            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
// //            await axios.put(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}`, {
// //                text: newText,
// //                updated_by: loggedInUserId
// //            });
// //
// //            // FIXED: Re-fetch and enrich the alert fully
// //            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));
// //            setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
// //            setEditingItem(null);
// //            alert(`Updated ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
// //        } catch (error) {
// //            console.error(`Error updating ${type}:`, error);
// //            alert(`Failed to update ${type === 'rec' ? 'recommendation' : 'note'}`);
// //        }
// //    };
// //
// //    // Delete Item
// //    const handleDeleteItem = (type, alertId, itemId) => {
// //        console.log('handleDeleteItem:', type, alertId, itemId);
// //        if (!alertId) {
// //            console.error('handleDeleteItem: Missing alertId');
// //            return;
// //        }
// //        setConfirmMessage(`Delete this ${type === 'rec' ? 'recommendation' : 'note'}?`);
// //        setConfirmAction({ type, alertId, itemId });
// //        setShowConfirm(true);
// //    };
// //
// //    // Confirm Delete
// //    const handleConfirmDelete = async () => {
// //        if (!confirmAction) return;
// //        const { type, alertId, itemId } = confirmAction;
// //        
// //        const taskId = await getTaskId(alertId);
// //        if (!taskId) {
// //            setShowConfirm(false);
// //            setConfirmAction(null);
// //            setConfirmMessage('');
// //            return;
// //        }
// //
// //        try {
// //            const endpoint = type === 'rec' ? 'recommendations' : 'notes';
// //            await axios.delete(`${API_BASE_URL}/tasks/${taskId}/${endpoint}/${itemId}?deleted_by=${loggedInUserId}`);
// //            
// //            // FIXED: Re-fetch and enrich the alert fully
// //            const enrichedAlert = await enrichAlertWithLatestTask(initialAlerts.find(a => a.id === alertId));
// //            setAlerts(prev => prev.map(a => a.id === alertId ? enrichedAlert : a));
// //            alert(`Deleted ${type === 'rec' ? 'recommendation' : 'note'} successfully`);
// //        } catch (error) {
// //            console.error(`Error deleting ${type}:`, error);
// //            alert(`Failed to delete ${type === 'rec' ? 'recommendation' : 'note'}`);
// //        }
// //        
// //        // Close dialog
// //        setShowConfirm(false);
// //        setConfirmAction(null);
// //        setConfirmMessage('');
// //    };
// //
// //    // Cancel Delete
// //    const handleCancelDelete = () => {
// //        setShowConfirm(false);
// //        setConfirmAction(null);
// //        setConfirmMessage('');
// //    };
// //    
// //    // Format Recommendations
// //    const formatRecommendations = (items, alertId) => {
// //        console.log('formatRecommendations called with alertId:', alertId, 'items:', items);
// //    
// //        return (
// //            <div className="text-xs text-gray-700 space-y-1">
// //                {/* Add Input */}
// //                <div className="flex space-x-1 mb-1">
// //                    <input
// //                        type="text"
// //                        value={addingRecText[alertId] || ''}
// //                        onChange={(e) => setAddingRecText(prev => ({ ...prev, [alertId]: e.target.value }))}
// //                        placeholder="Add recommendation.."
// //                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
// //                    />
// //                    <button
// //                        onClick={() => handleAddItem('rec', alertId, addingRecText[alertId] || '')}
// //                        disabled={!(addingRecText[alertId] || '').trim()}
// //                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
// //                    >
// //                        Add
// //                    </button>
// //                </div>
// //
// //                {/* List existing items OR show "No recommendations" */}
// //                {items && items.length > 0 ? (
// //                    <ul className="space-y-0.5 max-h-16 overflow-y-auto">
// //                        {items.map((item) => {
// //                            const isEditing = editingItem?.type === 'rec' && editingItem?.itemId === item.id;
// //                            return (
// //                                <li key={item.id} className="flex justify-between items-center space-x-2">
// //                                    {isEditing ? (
// //                                        <div className="flex space-x-1 flex-1">
// //                                            <input
// //                                                type="text"
// //                                                value={editingItem.text}
// //                                                onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
// //                                                className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
// //                                            />
// //                                            <button
// //                                                onClick={() => handleSaveEdit('rec', item.id, editingItem.text)}
// //                                                className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
// //                                            >
// //                                                Save
// //                                            </button>
// //                                            <button
// //                                                onClick={() => setEditingItem(null)}
// //                                                className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
// //                                            >
// //                                                Cancel
// //                                            </button>
// //                                        </div>
// //                                    ) : (
// //                                        <div className="flex-1">
// //                                            <span className="font-medium">{item.text}</span>
// //                                            <span className="text-gray-400 text-[10px] ml-1">
// //                                                ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
// //                                            </span>
// //                                        </div>
// //                                    )}
// //                                    {!isEditing && (
// //                                        <div className="space-x-1">
// //                                            <button
// //                                                onClick={() => handleEditItem('rec', alertId, item.id, item.text)}
// //                                                className="text-blue-500 text-[10px] hover:text-blue-700"
// //                                            >
// //                                                Edit
// //                                            </button>
// //                                            <button
// //                                                onClick={() => handleDeleteItem('rec', alertId, item.id)}
// //                                                className="text-red-500 text-[10px] hover:text-red-700"
// //                                            >
// //                                                Delete
// //                                            </button>
// //                                        </div>
// //                                    )}
// //                                </li>
// //                            );
// //                        })}
// //                    </ul>
// //                ) : (
// //                    <span className="text-gray-500 italic text-xs">No recommendations</span>
// //                )}
// //            </div>
// //        );
// //    };
// //
// //    // Format Notes
// //    const formatNotes = (items, alertId) => {
// //        console.log('formatNotes called with alertId:', alertId, 'items:', items);
// //        
// //        return (
// //            <div className="text-xs text-gray-700 space-y-1">
// //                {/* Add Input */}
// //                <div className="flex space-x-1 mb-1">
// //                    <input
// //                        type="text"
// //                        value={addingNoteText[alertId] || ''}
// //                        onChange={(e) => setAddingNoteText(prev => ({ ...prev, [alertId]: e.target.value }))}
// //                        placeholder="Add note..."
// //                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
// //                    />
// //                    <button
// //                        onClick={() => handleAddItem('note', alertId, addingNoteText[alertId] || '')}
// //                        disabled={!(addingNoteText[alertId] || '').trim()}
// //                        className="bg-green-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
// //                    >
// //                        Add
// //                    </button>
// //                </div>
// //
// //                {/* List existing items OR show "No notes" */}
// //                {items && items.length > 0 ? (
// //                    <ul className="space-y-0.5 max-h-16 overflow-y-auto">
// //                        {items.map((item) => {
// //                            const isEditing = editingItem?.type === 'note' && editingItem?.itemId === item.id;
// //                            return (
// //                                <li key={item.id} className="flex justify-between items-center space-x-2">
// //                                    {isEditing ? (
// //                                        <div className="flex space-x-1 flex-1">
// //                                            <input
// //                                                type="text"
// //                                                value={editingItem.text}
// //                                                onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
// //                                                className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
// //                                            />
// //                                            <button
// //                                                onClick={() => handleSaveEdit('note', item.id, editingItem.text)}
// //                                                className="bg-green-500 text-white px-1 py-0.5 rounded text-xs"
// //                                            >
// //                                                Save
// //                                            </button>
// //                                            <button
// //                                                onClick={() => setEditingItem(null)}
// //                                                className="bg-gray-500 text-white px-1 py-0.5 rounded text-xs"
// //                                            >
// //                                                Cancel
// //                                            </button>
// //                                        </div>
// //                                    ) : (
// //                                        <div className="flex-1">
// //                                            <span className="font-medium">{item.text}</span>
// //                                            <span className="text-gray-400 text-[10px] ml-1">
// //                                                ({item.created_by_name}, {new Date(item.created_at).toLocaleDateString()})
// //                                            </span>
// //                                        </div>
// //                                    )}
// //                                    {!isEditing && (
// //                                        <div className="space-x-1">
// //                                            <button
// //                                                onClick={() => handleEditItem('note', alertId, item.id, item.text)}
// //                                                className="text-blue-500 text-[10px] hover:text-blue-700"
// //                                            >
// //                                                Edit
// //                                            </button>
// //                                            <button
// //                                                onClick={() => handleDeleteItem('note', alertId, item.id)}
// //                                                className="text-red-500 text-[10px] hover:text-red-700"
// //                                            >
// //                                                Delete
// //                                            </button>
// //                                        </div>
// //                                    )}
// //                                </li>
// //                            );
// //                        })}
// //                    </ul>
// //                ) : (
// //                    <span className="text-gray-500 italic text-xs">No notes</span>
// //                )}
// //            </div>
// //        );
// //    };
// //
// //    // Helper functions (formatDate, formatTime) - moved here as they're used in alerts table
// //    const formatDate = (dateString) => {
// //        if (!dateString) return 'N/A';
// //        try {
// //            const date = new Date(dateString);
// //            return date.toLocaleDateString();
// //        } catch {
// //            return 'N/A';
// //        }
// //    };
// //
// //    const formatTime = (dateString) => {
// //        if (!dateString) return 'N/A';
// //        try {
// //            const date = new Date(dateString);
// //            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// //        } catch {
// //            return 'N/A';
// //        }
// //    };
// //
// //    return (
// //        <div>
// //            <div className="flex items-center justify-between mb-4">
// //                <h3 className="text-xl font-bold">Alerts</h3>
// //                <button
// //                    onClick={handleShowResolvedAlerts}
// //                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
// //                    disabled={resolvedAlertsLoading}
// //                >
// //                    {resolvedAlertsLoading ? 'Loading...' : 'View Resolved Alerts'}
// //                </button>
// //            </div>
// //
// //            <div className="overflow-x-auto">
// //                <table className="w-full">
// //                    <thead className="bg-gray-50">
// //                        <tr>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Priority</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Description</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Time</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Recommendations</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Notes</th>
// //                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
// //                        </tr>
// //                    </thead>
// //                    <tbody className="divide-y divide-gray-200">
// //                        {initialAlerts.length > 0 ? (
// //                            initialAlerts.map((alert, index) => {
// //                                const latestTask = alert.latestTask || {};
// //                                const recs = latestTask.recommendations || [];
// //                                const notes = latestTask.notes || [];
// //
// //                                console.log(`Alert ${alert.id}: recs=${recs.length}, notes=${notes.length}`);
// //
// //                                return (
// //                                    <tr key={index} className="hover:bg-gray-50">
// //                                        <td className="px-4 py-3 text-sm text-gray-900">{alert.priority}</td>
// //                                        <td className="px-4 py-3 text-sm text-gray-900">{alert.description}</td>
// //                                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(alert.created_at)}</td>
// //                                        <td className="px-4 py-3 text-sm text-gray-600">{formatTime(alert.created_at)}</td>
// //                                        <td className="px-4 py-3">
// //                                            {formatRecommendations(recs, alert.id)}
// //                                        </td>
// //                                        <td className="px-4 py-3">
// //                                            {formatNotes(notes, alert.id)}
// //                                        </td>
// //                                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
// //                                            <select
// //                                                className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500"
// //                                                value={alert.current_status || (alert.is_acknowledged ? 'Acknowledged' : 'Pending')}
// //                                                onChange={(e) => handleUpdateAlertStatus(alert.id, e.target.value)}
// //                                            >
// //                                                <option value="Pending">Pending</option>
// //                                                <option value="Acknowledged">Acknowledged</option>
// //                                                <option value="Resolved">Resolved</option>
// //                                            </select>
// //                                        </td>
// //                                    </tr>
// //                                );
// //                            })
// //                        ) : (
// //                            <tr>
// //                                <td colSpan={7} className="text-center py-4 text-gray-500">
// //                                    No alerts found for this patient.
// //                                </td>
// //                            </tr>
// //                        )}
// //                    </tbody>
// //                </table>
// //            </div>
// //
// //            {/* Confirm Dialog */}
// //            {showConfirm && (
// //                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
// //                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
// //                        <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
// //                        <p className="text-gray-600 mb-6">{confirmMessage}</p>
// //                        <div className="flex justify-end space-x-3">
// //                            <button
// //                                onClick={handleCancelDelete}
// //                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
// //                            >
// //                                Cancel
// //                            </button>
// //                            <button
// //                                onClick={handleConfirmDelete}
// //                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
// //                            >
// //                                Delete
// //                            </button>
// //                        </div>
// //                    </div>
// //                </div>
// //            )}
// //
// //            {showResolvedModal && (
// //                <ResolvedAlertsModal 
// //                    onClose={() => setShowResolvedModal(false)} 
// //                    resolvedAlerts={allResolvedAlertsData}
// //                />
// //            )}
// //        </div>
// //    );
// //};
// //
// //export default AlertsTab;
